/**
 * Interactive Content Hook
 * React hook for managing surveys, polls, and quizzes
 */

import { useState, useEffect, useCallback } from 'react';
import interactiveContentAPI, {
  InteractiveContent,
  ContentType,
  ContentStatus,
  CreateContentRequest,
  UpdateContentRequest,
  ContentLink,
  CreateLinkRequest,
  ContentTranslation,
  CreateTranslationRequest,
  ContentResponse,
  ContentAnalytics,
  ListContentParams,
} from '@/services/api/interactiveContent';

interface UseInteractiveContentOptions {
  content_type?: ContentType;
  status?: ContentStatus;
  search?: string;
  autoFetch?: boolean;
}

interface UseInteractiveContentReturn {
  items: InteractiveContent[];
  total: number;
  loading: boolean;
  error: string | null;

  // CRUD operations
  createContent: (data: CreateContentRequest) => Promise<InteractiveContent | null>;
  updateContent: (id: number, data: UpdateContentRequest, createNewVersion?: boolean) => Promise<InteractiveContent | null>;
  deleteContent: (id: number, hardDelete?: boolean) => Promise<boolean>;
  getContent: (id: number) => Promise<InteractiveContent | null>;
  cloneContent: (id: number, newName?: string) => Promise<InteractiveContent | null>;
  getVersions: (id: number) => Promise<InteractiveContent[]>;

  // Links
  createLink: (contentId: number, data: CreateLinkRequest) => Promise<ContentLink | null>;
  listLinks: (contentId: number) => Promise<ContentLink[]>;
  deleteLink: (linkId: number) => Promise<boolean>;

  // Translations
  addTranslation: (contentId: number, data: CreateTranslationRequest) => Promise<ContentTranslation | null>;
  getTranslations: (contentId: number) => Promise<ContentTranslation[]>;

  // Responses
  listResponses: (contentId: number, params?: { status?: string; limit?: number; offset?: number }) => Promise<{ responses: ContentResponse[]; total: number }>;
  getResponse: (contentId: number, responseId: number) => Promise<ContentResponse | null>;

  // Analytics
  getAnalytics: (contentId: number) => Promise<ContentAnalytics | null>;

  // Media Library
  addToMediaLibrary: (contentId: number, folderId?: number) => Promise<{ media_asset_id: number; short_url: string } | null>;

  // Refresh
  refetch: () => Promise<void>;
  setPage: (offset: number) => void;
}

export default function useInteractiveContent(
  options: UseInteractiveContentOptions = {}
): UseInteractiveContentReturn {
  const { content_type, status, search, autoFetch = true } = options;

  const [items, setItems] = useState<InteractiveContent[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchContent = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params: ListContentParams = {
        content_type,
        status,
        search,
        limit: 50,
        offset,
      };
      const data = await interactiveContentAPI.list(params);
      setItems(data.items);
      setTotal(data.total);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error
        ? err.message
        : 'Failed to fetch interactive content';
      setError(errorMessage);
      console.error('Error fetching interactive content:', err);
    } finally {
      setLoading(false);
    }
  }, [content_type, status, search, offset]);

  useEffect(() => {
    if (autoFetch) {
      fetchContent();
    }
  }, [autoFetch, fetchContent]);

  const createContent = async (data: CreateContentRequest): Promise<InteractiveContent | null> => {
    setLoading(true);
    setError(null);

    try {
      const newContent = await interactiveContentAPI.create(data);
      setItems((prev) => [newContent, ...prev]);
      setTotal((prev) => prev + 1);
      return newContent;
    } catch (err: unknown) {
      const errorMessage = err instanceof Error
        ? err.message
        : 'Failed to create content';
      setError(errorMessage);
      console.error('Error creating content:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateContent = async (
    id: number,
    data: UpdateContentRequest,
    createNewVersion = false
  ): Promise<InteractiveContent | null> => {
    setLoading(true);
    setError(null);

    try {
      const updated = await interactiveContentAPI.update(id, data, createNewVersion);
      if (createNewVersion) {
        // New version created - add to list
        setItems((prev) => [updated, ...prev.filter((i) => i.id !== id)]);
      } else {
        // Updated existing
        setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...updated } : i)));
      }
      return updated;
    } catch (err: unknown) {
      const errorMessage = err instanceof Error
        ? err.message
        : 'Failed to update content';
      setError(errorMessage);
      console.error('Error updating content:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const deleteContent = async (id: number, hardDelete = false): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      await interactiveContentAPI.delete(id, hardDelete);
      setItems((prev) => prev.filter((i) => i.id !== id));
      setTotal((prev) => prev - 1);
      return true;
    } catch (err: unknown) {
      const errorMessage = err instanceof Error
        ? err.message
        : 'Failed to delete content';
      setError(errorMessage);
      console.error('Error deleting content:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const getContent = async (id: number): Promise<InteractiveContent | null> => {
    try {
      return await interactiveContentAPI.get(id);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error
        ? err.message
        : 'Failed to get content';
      setError(errorMessage);
      console.error('Error getting content:', err);
      return null;
    }
  };

  const cloneContent = async (id: number, newName?: string): Promise<InteractiveContent | null> => {
    setLoading(true);
    setError(null);

    try {
      const cloned = await interactiveContentAPI.clone(id, newName);
      setItems((prev) => [cloned, ...prev]);
      setTotal((prev) => prev + 1);
      return cloned;
    } catch (err: unknown) {
      const errorMessage = err instanceof Error
        ? err.message
        : 'Failed to clone content';
      setError(errorMessage);
      console.error('Error cloning content:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const getVersions = async (id: number): Promise<InteractiveContent[]> => {
    try {
      return await interactiveContentAPI.getVersions(id);
    } catch (err: unknown) {
      console.error('Error getting versions:', err);
      return [];
    }
  };

  // Link operations
  const createLink = async (contentId: number, data: CreateLinkRequest): Promise<ContentLink | null> => {
    try {
      return await interactiveContentAPI.createLink(contentId, data);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error
        ? err.message
        : 'Failed to create link';
      setError(errorMessage);
      console.error('Error creating link:', err);
      return null;
    }
  };

  const listLinks = async (contentId: number): Promise<ContentLink[]> => {
    try {
      return await interactiveContentAPI.listLinks(contentId);
    } catch (err: unknown) {
      console.error('Error listing links:', err);
      return [];
    }
  };

  const deleteLink = async (linkId: number): Promise<boolean> => {
    try {
      await interactiveContentAPI.deleteLink(linkId);
      return true;
    } catch (err: unknown) {
      console.error('Error deleting link:', err);
      return false;
    }
  };

  // Translation operations
  const addTranslation = async (
    contentId: number,
    data: CreateTranslationRequest
  ): Promise<ContentTranslation | null> => {
    try {
      return await interactiveContentAPI.addTranslation(contentId, data);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error
        ? err.message
        : 'Failed to add translation';
      setError(errorMessage);
      console.error('Error adding translation:', err);
      return null;
    }
  };

  const getTranslations = async (contentId: number): Promise<ContentTranslation[]> => {
    try {
      return await interactiveContentAPI.getTranslations(contentId);
    } catch (err: unknown) {
      console.error('Error getting translations:', err);
      return [];
    }
  };

  // Response operations
  const listResponses = async (
    contentId: number,
    params?: { status?: string; limit?: number; offset?: number }
  ): Promise<{ responses: ContentResponse[]; total: number }> => {
    try {
      const data = await interactiveContentAPI.listResponses(contentId, params);
      return { responses: data.responses, total: data.total };
    } catch (err: unknown) {
      console.error('Error listing responses:', err);
      return { responses: [], total: 0 };
    }
  };

  const getResponse = async (contentId: number, responseId: number): Promise<ContentResponse | null> => {
    try {
      return await interactiveContentAPI.getResponse(contentId, responseId);
    } catch (err: unknown) {
      console.error('Error getting response:', err);
      return null;
    }
  };

  // Analytics
  const getAnalytics = async (contentId: number): Promise<ContentAnalytics | null> => {
    try {
      return await interactiveContentAPI.getAnalytics(contentId);
    } catch (err: unknown) {
      console.error('Error getting analytics:', err);
      return null;
    }
  };

  // Media Library
  const addToMediaLibrary = async (
    contentId: number,
    folderId?: number
  ): Promise<{ media_asset_id: number; short_url: string } | null> => {
    try {
      const result = await interactiveContentAPI.addToMediaLibrary(contentId, folderId);
      return { media_asset_id: result.media_asset_id, short_url: result.short_url };
    } catch (err: unknown) {
      const errorMessage = err instanceof Error
        ? err.message
        : 'Failed to add to media library';
      setError(errorMessage);
      console.error('Error adding to media library:', err);
      return null;
    }
  };

  return {
    items,
    total,
    loading,
    error,
    createContent,
    updateContent,
    deleteContent,
    getContent,
    cloneContent,
    getVersions,
    createLink,
    listLinks,
    deleteLink,
    addTranslation,
    getTranslations,
    listResponses,
    getResponse,
    getAnalytics,
    addToMediaLibrary,
    refetch: fetchContent,
    setPage: setOffset,
  };
}
